import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  Shield, ShieldAlert, ShieldCheck, Lock, Unlock, Activity, Eye,
  FileText, Loader2, ChevronRight, ArrowLeft, AlertTriangle, Clock,
  CheckCircle2, XCircle, Plus, Search, RefreshCw, Download,
  Database, Brain, Cpu, Key, FileCheck, BarChart3, Layers, Zap,
  Building2, Globe, Users
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

const THREAT_COLORS = {
  normal: { bg: "bg-emerald-400/10", text: "text-emerald-400", border: "border-emerald-400/20", label: "Normal" },
  guarded: { bg: "bg-[#36A9E1]/10", text: "text-[#36A9E1]", border: "border-[#36A9E1]/20", label: "Guarded" },
  elevated: { bg: "bg-amber-400/10", text: "text-amber-400", border: "border-amber-400/20", label: "Elevated" },
  critical: { bg: "bg-red-400/10", text: "text-red-400", border: "border-red-400/20", label: "Critical" },
};

const SEVERITY_COLORS = {
  info: "text-[#36A9E1]",
  low: "text-emerald-400",
  medium: "text-amber-400",
  warning: "text-orange-400",
  high: "text-red-400",
  critical: "text-red-500",
};

const ALCOA_LABELS = [
  { key: "attributable", label: "Attributable", desc: "Tied to authenticated user" },
  { key: "legible", label: "Legible", desc: "Standardized templates" },
  { key: "contemporaneous", label: "Contemporaneous", desc: "Server-side UTC timestamps" },
  { key: "original", label: "Original", desc: "First-entry preserved" },
  { key: "accurate", label: "Accurate", desc: "Input validation active" },
  { key: "complete", label: "Complete", desc: "Required fields enforced" },
  { key: "consistent", label: "Consistent", desc: "Template-driven entry" },
  { key: "enduring", label: "Enduring", desc: "Backed up & exportable" },
  { key: "available", label: "Available", desc: "API access anytime" },
];

/* ═══════════════════════════════════════════════════════════════ */
const ComplianceNavigatorPage = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sub-data
  const [auditLogs, setAuditLogs] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [aiRegistry, setAiRegistry] = useState(null);
  const [validationReports, setValidationReports] = useState([]);
  const [docPolicies, setDocPolicies] = useState([]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ax.get(`${API}/compliance/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      console.error("Dashboard load error:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const loadTab = useCallback(async (t) => {
    setTab(t);
    try {
      if (t === "audit-log") {
        const res = await ax.get(`${API}/compliance/audit-log?limit=100`);
        setAuditLogs(res.data.logs || []);
      } else if (t === "security") {
        const res = await ax.get(`${API}/compliance/security/events?limit=50`);
        setSecurityEvents(res.data.events || []);
      } else if (t === "ai-governance") {
        const res = await ax.get(`${API}/compliance/ai-governance?limit=100`);
        setAiRegistry(res.data);
      } else if (t === "validation") {
        const res = await ax.get(`${API}/compliance/validation-reports`);
        setValidationReports(res.data.reports || []);
      } else if (t === "policies") {
        const res = await ax.get(`${API}/compliance/document-policies`);
        setDocPolicies(res.data.policies || []);
      }
    } catch {}
  }, []);

  if (loading) {
    return (
      <QMSLayout title={t('navigator.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  const threat = THREAT_COLORS[dashboard?.security?.threat_level] || THREAT_COLORS.normal;

  const tabs = [
    { id: "dashboard", label: t('dashboard:navigatorTabs.overview'), icon: BarChart3 },
    { id: "security", label: t('dashboard:navigatorTabs.security'), icon: ShieldAlert },
    { id: "audit-log", label: t('dashboard:navigatorTabs.auditLog'), icon: FileText },
    { id: "ai-governance", label: t('dashboard:navigatorTabs.aiGovernance'), icon: Brain },
    { id: "validation", label: t('dashboard:navigatorTabs.validation'), icon: FileCheck },
    { id: "policies", label: t('dashboard:navigatorTabs.policies'), icon: Layers },
  ];

  return (
    <QMSLayout title={t('dashboard:navigator.title')}>
      <div className="space-y-6" data-testid="compliance-navigator">
        {/* Tab Bar */}
        <div className="flex gap-1 bg-[#181B23] rounded-lg p-1 overflow-x-auto" data-testid="compliance-tabs">
          {tabs.map(tabItem => {
            const Icon = tabItem.icon;
            const active = tab === tabItem.id;
            return (
              <button
                key={tabItem.id}
                onClick={() => loadTab(tabItem.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded text-sm whitespace-nowrap transition-colors ${
                  active ? "bg-[#36A9E1]/10 text-[#36A9E1]" : "text-[#8B95A5] hover:text-white hover:bg-[#1F222D]"
                }`}
                data-testid={`tab-${tabItem.id}`}
              >
                <Icon className="w-4 h-4" />
                {tabItem.label}
              </button>
            );
          })}
        </div>

        {/* ═══ DASHBOARD TAB ═══ */}
        {tab === "dashboard" && dashboard && (
          <div className="space-y-6">
            {/* Threat Level Banner */}
            <div className={`${threat.bg} border ${threat.border} rounded-lg p-5 flex items-center justify-between`} data-testid="threat-level-banner">
              <div className="flex items-center gap-4">
                <Shield className={`w-10 h-10 ${threat.text}`} />
                <div>
                  <div className={`text-lg font-bold ${threat.text}`}>{t('dashboard:complianceNav.threatLevel')}: {threat.label}</div>
                  <p className="text-sm text-[#8B95A5]">
                    {dashboard.security.open_events} {t('dashboard:complianceNav.openEvents')} | {dashboard.security.critical_events} {t('dashboard:complianceNav.critical')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded ${threat.bg} ${threat.text}`}>
                  {dashboard.security.self_lock_active ? t('dashboard:complianceNav.selfLockActive') : t('dashboard:complianceNav.systemsNormal')}
                </span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <DashCard label="Audit Log (7d)" value={dashboard.audit_log.entries_7d} icon={FileText} color="text-[#36A9E1]" />
              <DashCard label="AI Calls (30d)" value={dashboard.ai_governance.calls_30d} icon={Brain} color="text-purple-400" />
              <DashCard label="Validation Reports" value={dashboard.validation.total_reports} icon={FileCheck} color="text-emerald-400" />
              <DashCard label="Policies" value={dashboard.policies.total} icon={Layers} color="text-amber-400" />
            </div>

            {/* P2B Compliance Modules */}
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5" data-testid="p2b-modules">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#36A9E1]" />
                P2B Compliance Modules
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Document Redaction", desc: "PII detection & redaction engine", icon: Eye, href: "/redaction", color: "text-red-400" },
                  { label: "SCRIBER Disclosures", desc: "Versioned disclosure templates", icon: FileText, href: "/scriber-disclosures", color: "text-amber-400" },
                  { label: "SSO Providers", desc: "OIDC/SAML connector admin", icon: Globe, href: "/sso-providers", color: "text-purple-400", adminOnly: true },
                  { label: "Vendor Registration", desc: "Vendor lifecycle & review", icon: Building2, href: "/vendor-registration", color: "text-emerald-400" },
                ].filter(m => !m.adminOnly || user?.role === "admin").map(m => {
                  const Icon = m.icon;
                  return (
                    <a key={m.label} href={m.href}
                      className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38] hover:border-[#36A9E1]/30 transition-colors group"
                      data-testid={`p2b-module-${m.href.slice(1)}`}
                    >
                      <Icon className={`w-6 h-6 ${m.color} mb-2 group-hover:scale-110 transition-transform`} />
                      <div className="text-white text-sm font-medium">{m.label}</div>
                      <div className="text-xs text-[#8B95A5] mt-1">{m.desc}</div>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* M6 P3 Readiness Modules */}
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5" data-testid="p3-modules">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#36A9E1]" />
                P3 Readiness Modules
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <a href="/analytics"
                  className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38] hover:border-[#36A9E1]/30 transition-colors group"
                  data-testid="p3-module-analytics"
                >
                  <BarChart3 className="w-6 h-6 text-[#36A9E1] mb-2 group-hover:scale-110 transition-transform" />
                  <div className="text-white text-sm font-medium">Analytics & Charts</div>
                  <div className="text-xs text-[#8B95A5] mt-1">Cross-module chart visualizations & trends</div>
                </a>
                <a href="/scar-escalation"
                  className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38] hover:border-red-400/30 transition-colors group"
                  data-testid="p3-module-scar-escalation"
                >
                  <Zap className="w-6 h-6 text-red-400 mb-2 group-hover:scale-110 transition-transform" />
                  <div className="text-white text-sm font-medium">SCAR Escalation</div>
                  <div className="text-xs text-[#8B95A5] mt-1">Automated escalation trigger framework</div>
                </a>
                <a href="/compliance-dashboard"
                  className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38] hover:border-emerald-400/30 transition-colors group"
                  data-testid="p3-module-compliance-dashboard"
                >
                  <Shield className="w-6 h-6 text-emerald-400 mb-2 group-hover:scale-110 transition-transform" />
                  <div className="text-white text-sm font-medium">Compliance Posture</div>
                  <div className="text-xs text-[#8B95A5] mt-1">Cross-module compliance dashboard analytics</div>
                </a>
              </div>
            </div>

            {/* ALCOA+ Status */}
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5" data-testid="alcoa-status">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <Database className="w-4 h-4 text-emerald-400" />
                ALCOA+ Data Integrity Status
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
                {ALCOA_LABELS.map(a => {
                  const ok = dashboard.alcoa_status?.[a.key];
                  return (
                    <div key={a.key} className={`text-center p-2 rounded border ${ok ? "border-emerald-400/20 bg-emerald-400/5" : "border-red-400/20 bg-red-400/5"}`}>
                      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto mb-1" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto mb-1" />}
                      <div className="text-xs text-white font-medium">{a.label}</div>
                      <div className="text-[10px] text-[#8B95A5] mt-0.5">{a.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Encryption Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4 text-[#E8A838]" />
                  Fort Knox Encryption
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#8B95A5]">At Rest</span>
                    <span className="text-emerald-400 font-mono text-xs">{dashboard.encryption.at_rest}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#8B95A5]">In Transit</span>
                    <span className="text-emerald-400 font-mono text-xs">{dashboard.encryption.in_transit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#8B95A5]">Status</span>
                    <span className="text-emerald-400 flex items-center gap-1"><Lock className="w-3 h-3" /> Active</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5" data-testid="compliance-standards">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#36A9E1]" />
                  Standards Coverage
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {["FDA QMSR", "21 CFR Part 11", "ISO 13485", "NIST CSF 2.0", "ICH Q7/Q9/Q10", "AS9100D", "IATF 16949", "SOC 2", "GDPR", "EU AI Act"].map(s => (
                    <span key={s} className="text-[10px] px-2 py-0.5 rounded bg-[#36A9E1]/10 text-[#36A9E1]">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ SECURITY TAB ═══ */}
        {tab === "security" && (
          <SecurityPanel events={securityEvents} onReload={() => loadTab("security")} user={user} />
        )}

        {/* ═══ AUDIT LOG TAB ═══ */}
        {tab === "audit-log" && (
          <AuditLogPanel logs={auditLogs} onReload={() => loadTab("audit-log")} />
        )}

        {/* ═══ AI GOVERNANCE TAB ═══ */}
        {tab === "ai-governance" && (
          <AIGovernancePanel data={aiRegistry} onReload={() => loadTab("ai-governance")} />
        )}

        {/* ═══ VALIDATION TAB ═══ */}
        {tab === "validation" && (
          <ValidationPanel reports={validationReports} onReload={() => loadTab("validation")} user={user} />
        )}

        {/* ═══ POLICIES TAB ═══ */}
        {tab === "policies" && (
          <PoliciesPanel policies={docPolicies} onReload={() => loadTab("policies")} user={user} />
        )}
      </div>
    </QMSLayout>
  );
};

/* ─── Dashboard Card ───────────────────────────────────────── */
const DashCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 flex items-center gap-3" data-testid={`dash-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
    <Icon className={`w-5 h-5 ${color}`} />
    <div>
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-xs text-[#8B95A5]">{label}</div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════ */
/* SECURITY PANEL                                                   */
/* ═══════════════════════════════════════════════════════════════ */
const SecurityPanel = ({ events, onReload, user }) => {
  const [reporting, setReporting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ event_type: "manual_report", severity: "medium", description: "" });

  const handleReport = async () => {
    if (!form.description.trim()) return;
    setReporting(true);
    try {
      await ax.post(`${API}/compliance/security/report-event`, form);
      setShowForm(false);
      setForm({ event_type: "manual_report", severity: "medium", description: "" });
      onReload();
    } catch {}
    setReporting(false);
  };

  const resolveEvent = async (eventId) => {
    try {
      await ax.put(`${API}/compliance/security/events/${eventId}`, { status: "resolved", action: "Resolved by admin" });
      onReload();
    } catch {}
  };

  return (
    <div className="space-y-4" data-testid="security-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-red-400" /> Security Events
        </h3>
        <div className="flex gap-2">
          <button onClick={onReload} className="p-1.5 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white" data-testid="security-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          {user?.role === "admin" && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-xs"
              data-testid="report-event-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Report Event
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="bg-[#1F222D] border border-red-400/20 rounded-lg p-4 space-y-3" data-testid="report-event-form">
          <select value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })}
            className="w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white">
            <option value="manual_report">Manual Report</option>
            <option value="failed_login_spike">Failed Login Spike</option>
            <option value="api_anomaly">API Rate Anomaly</option>
            <option value="data_access_anomaly">Data Access Anomaly</option>
            <option value="unauthorized_access">Unauthorized Access Attempt</option>
          </select>
          <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
            className="w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="Describe the security event..." rows={3}
            className="w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5]" />
          <div className="flex gap-2">
            <button onClick={handleReport} disabled={reporting}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs disabled:opacity-50" data-testid="submit-event-btn">
              {reporting ? t('dashboard:complianceNav.reporting') : t('common:actions.submit', 'Submit')}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-1.5 bg-[#2D313D] text-[#8B95A5] rounded text-xs">{t('common:actions.cancel')}</button>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="text-center py-12 text-[#8B95A5]">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{t('dashboard:complianceNav.noSecurityEvents')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(evt => (
            <div key={evt.event_id} className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4" data-testid={`event-${evt.event_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-4 h-4 ${SEVERITY_COLORS[evt.severity] || "text-[#8B95A5]"}`} />
                  <span className="text-sm text-white">{evt.event_type.replace(/_/g, " ").toUpperCase()}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${evt.status === "open" ? "bg-red-400/10 text-red-400" : "bg-emerald-400/10 text-emerald-400"}`}>
                    {evt.status}
                  </span>
                </div>
                {evt.status === "open" && user?.role === "admin" && (
                  <button onClick={() => resolveEvent(evt.event_id)}
                    className="text-xs text-emerald-400 hover:text-white px-2 py-1 bg-emerald-400/10 rounded" data-testid={`resolve-${evt.event_id}`}>
                    Resolve
                  </button>
                )}
              </div>
              <p className="text-xs text-[#8B95A5] mt-1">{evt.description}</p>
              <div className="flex items-center gap-3 mt-2 text-[10px] text-[#8B95A5]">
                <span>{new Date(evt.detected_at).toLocaleString()}</span>
                <span>by {evt.reporter_name || "System"}</span>
                <span className={SEVERITY_COLORS[evt.severity]}>{evt.severity}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/* AUDIT LOG PANEL                                                  */
/* ═══════════════════════════════════════════════════════════════ */
const AuditLogPanel = ({ logs, onReload }) => {
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const verifyChain = async () => {
    setVerifying(true);
    try {
      const res = await ax.get(`${API}/compliance/audit-log/verify`);
      setVerifyResult(res.data);
    } catch {}
    setVerifying(false);
  };

  return (
    <div className="space-y-4" data-testid="audit-log-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#36A9E1]" /> ALCOA+ Compliance Audit Log
        </h3>
        <div className="flex gap-2">
          <button onClick={verifyChain} disabled={verifying}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-400 rounded text-xs disabled:opacity-50"
            data-testid="verify-chain-btn">
            {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            Verify Chain
          </button>
          <button onClick={onReload} className="p-1.5 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {verifyResult && (
        <div className={`p-3 rounded text-sm ${verifyResult.verified ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20" : "bg-red-400/10 text-red-400 border border-red-400/20"}`} data-testid="verify-result">
          {verifyResult.verified ? <CheckCircle2 className="w-4 h-4 inline mr-2" /> : <XCircle className="w-4 h-4 inline mr-2" />}
          {verifyResult.message} ({verifyResult.entries_checked} entries checked)
        </div>
      )}

      {logs.length === 0 ? (
        <div className="text-center py-12 text-[#8B95A5]">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No audit log entries yet</p>
        </div>
      ) : (
        <div className="space-y-1">
          {logs.map((log, idx) => (
            <div key={log.log_id || idx} className="bg-[#1F222D] border border-[rgba(255,255,255,0.06)] rounded px-4 py-2.5 flex items-center gap-3 text-xs" data-testid={`log-${log.log_id}`}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEVERITY_COLORS[log.severity] ? SEVERITY_COLORS[log.severity].replace("text-", "bg-") : "bg-[#36A9E1]"}`} />
              <span className="text-[#8B95A5] w-36 flex-shrink-0 font-mono">{new Date(log.timestamp).toLocaleString()}</span>
              <span className="text-white w-28 flex-shrink-0 truncate">{log.user_name}</span>
              <span className="text-[#36A9E1] w-44 flex-shrink-0 truncate">{log.action}</span>
              <span className="text-[#8B95A5] flex-1 truncate">{log.details}</span>
              <span className="text-[#8B95A5] font-mono text-[10px] w-16 flex-shrink-0 truncate" title={log.integrity_hash}>
                #{log.integrity_hash?.slice(0, 8)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/* AI GOVERNANCE PANEL                                              */
/* ═══════════════════════════════════════════════════════════════ */
const AIGovernancePanel = ({ data, onReload }) => (
  <div className="space-y-4" data-testid="ai-governance-panel">
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2">
        <Brain className="w-4 h-4 text-purple-400" /> AI Governance Registry
      </h3>
      <button onClick={onReload} className="p-1.5 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white">
        <RefreshCw className="w-4 h-4" />
      </button>
    </div>

    {/* Feature Stats */}
    {data?.feature_stats?.length > 0 && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.feature_stats.map(fs => (
          <div key={fs.feature} className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-3">
            <div className="text-lg font-bold text-white">{fs.call_count}</div>
            <div className="text-xs text-[#8B95A5]">{fs.feature}</div>
            <div className="text-[10px] text-purple-400 mt-1">{fs.models_used?.join(", ")}</div>
          </div>
        ))}
      </div>
    )}

    {/* Entries */}
    {(!data?.entries || data.entries.length === 0) ? (
      <div className="text-center py-12 text-[#8B95A5]">
        <Brain className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No AI calls logged yet</p>
        <p className="text-xs mt-1">AI calls from War Room, SCRIBER, Forms, and Q&A will appear here</p>
      </div>
    ) : (
      <div className="space-y-1">
        {data.entries.map((e, i) => (
          <div key={e.entry_id || i} className="bg-[#1F222D] border border-[rgba(255,255,255,0.06)] rounded px-4 py-2.5 flex items-center gap-3 text-xs">
            <Cpu className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
            <span className="text-[#8B95A5] w-32 flex-shrink-0">{new Date(e.timestamp).toLocaleString()}</span>
            <span className="text-white w-32 flex-shrink-0">{e.feature}</span>
            <span className="text-purple-400 w-20 flex-shrink-0">{e.model}</span>
            <span className="text-[#8B95A5] w-24 flex-shrink-0">{e.user_name}</span>
            <span className={`w-12 flex-shrink-0 ${e.human_approved ? "text-emerald-400" : "text-amber-400"}`}>
              {e.human_approved ? "Approved" : "Pending"}
            </span>
            <span className="text-[#8B95A5] flex-1 truncate">{e.input_summary}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════════ */
/* VALIDATION PANEL                                                 */
/* ═══════════════════════════════════════════════════════════════ */
const ValidationPanel = ({ reports, onReload, user }) => {
  const [generating, setGenerating] = useState(false);

  const generateReport = async () => {
    setGenerating(true);
    try {
      await ax.post(`${API}/compliance/validation-reports/generate`, {
        version: "1.0",
        notes: "Routine system validation",
      });
      onReload();
    } catch (err) {
      alert("Failed to generate: " + (err.response?.data?.detail || err.message));
    }
    setGenerating(false);
  };

  const approveReport = async (reportId, type) => {
    try {
      await ax.put(`${API}/compliance/validation-reports/${reportId}/approve`, {
        approval_type: type,
        signature: user?.name,
      });
      onReload();
    } catch {}
  };

  return (
    <div className="space-y-4" data-testid="validation-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <FileCheck className="w-4 h-4 text-emerald-400" /> System Validation Reports (IQ/OQ/PQ)
        </h3>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <button onClick={generateReport} disabled={generating}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#36A9E1] hover:bg-[#36A9E1]/80 text-white rounded text-xs disabled:opacity-50"
              data-testid="generate-svr-btn">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Generate Report
            </button>
          )}
          <button onClick={onReload} className="p-1.5 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-12 text-[#8B95A5]">
          <FileCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No validation reports generated yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <div key={r.report_id} className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5" data-testid={`svr-${r.report_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-medium text-white">{r.report_id}</h4>
                  <p className="text-xs text-[#8B95A5]">Version {r.version} | {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  r.status === "approved" ? "bg-emerald-400/10 text-emerald-400" :
                  r.status === "partially_approved" ? "bg-amber-400/10 text-amber-400" :
                  "bg-[#36A9E1]/10 text-[#36A9E1]"
                }`}>{r.status?.replace(/_/g, " ")}</span>
              </div>

              {/* Qualification sections */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {["installation_qualification", "operational_qualification", "performance_qualification"].map(sec => {
                  const s = r.sections?.[sec];
                  return (
                    <div key={sec} className="bg-[#181B23] rounded p-2 text-center">
                      <CheckCircle2 className={`w-4 h-4 mx-auto mb-1 ${s?.status === "pass" ? "text-emerald-400" : "text-amber-400"}`} />
                      <div className="text-[10px] text-[#8B95A5]">{sec.split("_").map(w => w[0].toUpperCase() + w.slice(1)).join(" ")}</div>
                    </div>
                  );
                })}
              </div>

              {/* Approvals */}
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  {r.approvals?.charstan_qa?.approved ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  ) : (
                    user?.role === "admin" ? (
                      <button onClick={() => approveReport(r.report_id, "charstan_qa")}
                        className="text-[#36A9E1] hover:text-white underline" data-testid={`approve-qa-${r.report_id}`}>
                        Approve QA
                      </button>
                    ) : <Clock className="w-3 h-3 text-amber-400" />
                  )}
                  <span className="text-[#8B95A5]">CHARSTAN QA</span>
                </div>
                <div className="flex items-center gap-1">
                  {r.approvals?.client_admin?.approved ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  ) : (
                    user?.role === "admin" ? (
                      <button onClick={() => approveReport(r.report_id, "client_admin")}
                        className="text-[#36A9E1] hover:text-white underline" data-testid={`approve-client-${r.report_id}`}>
                        Approve Client
                      </button>
                    ) : <Clock className="w-3 h-3 text-amber-400" />
                  )}
                  <span className="text-[#8B95A5]">Client Admin</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/* POLICIES PANEL                                                   */
/* ═══════════════════════════════════════════════════════════════ */
const PoliciesPanel = ({ policies, onReload, user }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", document_type: "all",
    retention_days: 3650, expiration_enabled: false, expiration_days: 365,
    governing_standard: "", redaction_required: false,
  });

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await ax.post(`${API}/compliance/document-policies`, form);
      setShowForm(false);
      setForm({ name: "", description: "", document_type: "all", retention_days: 3650, expiration_enabled: false, expiration_days: 365, governing_standard: "", redaction_required: false });
      onReload();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="policies-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Layers className="w-4 h-4 text-amber-400" /> Document Lifecycle Policies
        </h3>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <button onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#36A9E1] hover:bg-[#36A9E1]/80 text-white rounded text-xs"
              data-testid="create-policy-btn">
              <Plus className="w-3.5 h-3.5" /> New Policy
            </button>
          )}
          <button onClick={onReload} className="p-1.5 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-[#1F222D] border border-[#36A9E1]/20 rounded-lg p-4 space-y-3" data-testid="policy-form">
          <input type="text" placeholder="Policy Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5]" />
          <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
            className="w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5]" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#8B95A5] mb-1 block">{t('dashboard:complianceNav.retentionDays')}</label>
              <input type="number" value={form.retention_days} onChange={e => setForm({ ...form, retention_days: parseInt(e.target.value) || 365 })}
                className="w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-[#8B95A5] mb-1 block">{t('dashboard:complianceNav.governingStandard')}</label>
              <input type="text" placeholder="e.g., ISO 13485 4.2.5" value={form.governing_standard} onChange={e => setForm({ ...form, governing_standard: e.target.value })}
                className="w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5]" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-xs text-[#CBD5E1] cursor-pointer">
              <input type="checkbox" checked={form.expiration_enabled} onChange={e => setForm({ ...form, expiration_enabled: e.target.checked })} className="rounded" />
              {t('dashboard:complianceNav.enableExpiration')}
            </label>
            <label className="flex items-center gap-1.5 text-xs text-[#CBD5E1] cursor-pointer">
              <input type="checkbox" checked={form.redaction_required} onChange={e => setForm({ ...form, redaction_required: e.target.checked })} className="rounded" />
              {t('dashboard:complianceNav.redactionRequired')}
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={saving}
              className="px-4 py-1.5 bg-[#36A9E1] hover:bg-[#36A9E1]/80 text-white rounded text-xs disabled:opacity-50" data-testid="save-policy-btn">
              {saving ? t('common:status.saving') : t('dashboard:complianceNav.createPolicy')}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-1.5 bg-[#2D313D] text-[#8B95A5] rounded text-xs">{t('common:actions.cancel')}</button>
          </div>
        </div>
      )}

      {policies.length === 0 ? (
        <div className="text-center py-12 text-[#8B95A5]">
          <Layers className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No document policies defined yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {policies.map(p => (
            <div key={p.policy_id} className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4" data-testid={`policy-${p.policy_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-sm font-medium text-white">{p.name}</h4>
                  <p className="text-xs text-[#8B95A5] mt-0.5">{p.description}</p>
                </div>
                <span className="text-xs text-[#8B95A5] font-mono">{p.policy_id}</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#8B95A5]">
                <span>Retention: {p.retention_days} days</span>
                {p.expiration_enabled && <span className="text-amber-400">Expiration: {p.expiration_days}d</span>}
                {p.redaction_required && <span className="text-red-400">Redaction Required</span>}
                {p.governing_standard && <span className="text-[#36A9E1]">{p.governing_standard}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComplianceNavigatorPage;
